import { createAsyncThunk } from '@reduxjs/toolkit';

import { HttpError } from '../../api/client';
import { API_ERROR_CODES, type EventCreateRequest, type EventDto } from '../../api/types';
import { guestAccessErrorMessage } from '../../domain/guestAccess/errors';
import { entitiesActions } from '../slices/entitiesSlice';
import { uiActions } from '../slices/uiSlice';
import type { RootState } from '../store';

import { eventsService } from './_deps';

export const createEvent = createAsyncThunk<EventDto, EventCreateRequest, { state: RootState }>(
  'events/create',
  async (payload, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    try {
      const event = await eventsService.createEvent({ payload });
      dispatch(entitiesActions.upsertEvents([event]));

      // Auto-join + auto-confirm for creator.
      // Best-effort: creation should not be blocked by join/confirm failures.
      const me = getState().auth.user;
      const myTelegramId = me?.telegramId ?? null;
      const alreadyInParticipants = myTelegramId
        ? event.participants.some((p) => p.telegramId === myTelegramId)
        : false;

      // Проверяем, есть ли место в мероприятии
      const hasSpace = event.participants.length < event.maxParticipants;

      if (myTelegramId && !alreadyInParticipants && hasSpace) {
        try {
          const joinRes = await eventsService.joinEvent({
            eventId: event.id,
          });
          dispatch(entitiesActions.patchEvent(joinRes.event));

          const confirmRes = await eventsService.confirmEvent({
            eventId: event.id,
          });
          dispatch(entitiesActions.patchEvent(confirmRes.event));
        } catch (error) {
          // Silently ignore auto-join/confirm errors
        }
      }

      return event;
    } catch (error) {
      if (error instanceof HttpError && error.payload?.error === API_ERROR_CODES.TIME_CONFLICT) {
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: 'На это время уже запланировано другое мероприятие',
          }),
        );
      } else if (error instanceof HttpError && error.code?.startsWith('GUEST_ACCESS_')) {
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: guestAccessErrorMessage(error, 'Не удалось создать мероприятие'),
          }),
        );
      }
      throw error;
    }
  },
);
